// Sure Send Connect — public landing-page bundle.
//
// This is intentionally NOT the SPA. Connect pages are server-rendered ERB
// (SUR-1858); this bundle adds only two things on top:
//   1. A minimal Stimulus application for light interactivity (form validation,
//      galleries, toggles) — register controllers here, keep them small.
//   2. A React "island" mounter for the few genuinely interactive widgets
//      (map draw, CMA builder, embedded chat) that earn a component. Islands
//      hydrate in place via [data-connect-island]; there is no client router.
//
// Built as a separate esbuild entrypoint (see esbuild.config.js) so public
// pages never pull in the full app/SPA bundle.

import { Application } from "@hotwired/stimulus";
import LeadCaptureController from "./controllers/lead_capture_controller";
import ConnectRegistrationController from "./controllers/connect_registration_controller";
import ConnectRegistrationConfirmController from "./controllers/connect_registration_confirm_controller";
import ConnectRegGateController from "./controllers/connect_reg_gate_controller";
import ConnectSessionController from "./controllers/connect_session_controller";
import ConnectAccountController from "./controllers/connect_account_controller";
import ConnectContactController from "./controllers/connect_contact_controller";
import ConnectAffordabilityController from "./controllers/connect_affordability_controller";
import ConnectMortgageMiniController from "./controllers/connect_mortgage_mini_controller";
import ConnectMarketSnapshotController from "./controllers/connect_market_snapshot_controller";
import ConnectListingMapController from "./controllers/connect_listing_map_controller";
import ConnectPreapprovalController from "./controllers/connect_preapproval_controller";
import ConnectGalleryController from "./controllers/connect_gallery_controller";
import ConnectFavoritesController from "./controllers/connect_favorites_controller";
import ConnectReadMoreController from "./controllers/connect_read_more_controller";
import ConnectModeController from "./controllers/connect_mode_controller";
import { mountIslands } from "./islands";
import { connectPixelToken } from "./pixel_token";
import { connectBase, connectPath, connectPreview, connectStorageScope } from "./base_path";
import {
  claimConversionNow,
  conversionIdForRequest,
  successfulLeadRequest,
  successfulRegistrationRequest,
  trackAdvertisingLead,
  trackClaimedConversion,
} from "./tracking";

// Path-preview support (QA/dev): when the page is served under a
// /_connect/:slug prefix on the main app domain (<meta name="connect-base">,
// set by Middleware::ConnectPathPreview), rewrite root-relative same-origin
// fetches to carry the prefix. Every endpoint this bundle talks to is a
// root-relative path ("/_lead", "/account/session", …), so wrapping fetch once
// here covers all controllers and islands. On a real agent subdomain the base
// is "" and fetch is left untouched.
(function installConnectFetch() {
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const requestInput = connectBase() && typeof input === "string" ? connectPath(input) : input;
    const response = await originalFetch(requestInput, init);
    if (successfulLeadRequest(input, init, response)) {
      trackAdvertisingLead("lead", conversionIdForRequest(input, init));
    } else if (successfulRegistrationRequest(input, init, response)) {
      // In-place registrations (AVM gate, pre-approval) never load another
      // page, so claim the parked conversion now instead of waiting for a
      // page-view beacon that may never fire.
      claimConversionNow();
    }
    return response;
  };
})();

const application = Application.start();
application.debug = false;
application.register("lead-capture", LeadCaptureController);
application.register("connect-registration", ConnectRegistrationController);
application.register("connect-registration-confirm", ConnectRegistrationConfirmController);
application.register("connect-reg-gate", ConnectRegGateController);
application.register("connect-session", ConnectSessionController);
application.register("connect-account", ConnectAccountController);
application.register("connect-contact", ConnectContactController);
application.register("connect-affordability", ConnectAffordabilityController);
application.register("connect-mortgage-mini", ConnectMortgageMiniController);
application.register("connect-market-snapshot", ConnectMarketSnapshotController);
application.register("connect-listing-map", ConnectListingMapController);
application.register("connect-preapproval", ConnectPreapprovalController);
application.register("connect-gallery", ConnectGalleryController);
application.register("connect-favorites", ConnectFavoritesController);
application.register("connect-read-more", ConnectReadMoreController);
application.register("connect-mode", ConnectModeController);

// Forced-registration gate (SUR-1871): record a successful registration on this
// device. The confirm flow redirects to ?registered=1; persisting the flag here
// — on EVERY page load, even when no gate markup is rendered — means the gate
// stays suppressed even after the 24h server session lapses. Mirrors the keys
// in connect_reg_gate_controller.js (scoped per Connect site — host, plus the
// path-preview base when agents share the dev host — so agents count separately).
function recordConnectRegistration() {
  try {
    if (new URLSearchParams(window.location.search).get("registered") !== "1") return;
    window.localStorage.setItem(`ssc_reg:${connectStorageScope()}:registered`, "1");
    window.localStorage.removeItem(`ssc_reg:${connectStorageScope()}:views`);
  } catch (_e) {
    /* localStorage unavailable (private mode) — skip silently */
  }
}

// Per-agent page-view beacon (SUR-1918, SUR-1883). Same-origin POST to /_track
// on the Connect host. The server records subdomain analytics AND a contact
// timeline view — attributed to the signed-in registrant (credentials: include
// sends the host-scoped Connect session) or held on the PixelSession for
// backfill. On a listing page it carries the listing's MLS + specs so the view
// lands in the Property Activity tab. Best-effort and fully isolated — tracking
// must never break a public page render.
function trackConnectPageView() {
  // The editor preview renders this layout on the CRM host, where /_track lives
  // behind the Connect subdomain constraint and so 404s. Recording a page view
  // for an agent looking at their own draft would be wrong regardless — the
  // server takes the same position in ConnectHelper#connect_tracking_enabled?.
  if (connectPreview()) return;

  try {
    const fields = {
      referrer: document.referrer || "",
      url: window.location.href,
      title: document.title || "",
    };
    const token = connectPixelToken();
    if (token) fields.session_token = token;

    const ctx = document.getElementById("ssc-property-context");
    if (ctx) {
      fields.prop_mls = ctx.dataset.mls || "";
      fields.prop_url = window.location.href;
      fields.prop_address = ctx.dataset.address || "";
      fields.prop_price = ctx.dataset.price || "";
      fields.prop_beds = ctx.dataset.beds || "";
      fields.prop_baths = ctx.dataset.baths || "";
      fields.prop_sqft = ctx.dataset.sqft || "";
      fields.prop_type = ctx.dataset.type || "";
      fields.prop_image = ctx.dataset.image || "";
    }

    fetch("/_track", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(fields).toString(),
      keepalive: true,
      credentials: "include",
    })
      .then(trackClaimedConversion)
      .catch(() => {});
  } catch (_e) {
    /* sessionStorage/crypto unavailable (private mode) — skip silently */
  }
}

// Mount React islands once the DOM is ready. The bundle is loaded with `defer`,
// so DOMContentLoaded may already have fired by the time this runs.
function onReady() {
  mountIslands();
  trackConnectPageView();
  recordConnectRegistration();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", onReady);
} else {
  onReady();
}
